import { useMemo } from "react";
import { useQuery } from "react-query";
import { api } from "../api";
import { DeviceCatalogIndex, DeviceCatalogResponse } from "@tether-web-portal/types/DeviceTypeCapability";
import { indexByType, setDeviceCatalogCache } from "./deviceCatalogCache";

enum QueryKeys {
  DEVICE_CATALOG = "device-catalog",
}

/**
 * Device-type catalog (GET /v2/device-types) — the catalog-driven source of truth for device
 * capabilities and icon keys. The result is mirrored into the module-level cache
 * (`deviceCatalogCache`) so the synchronous device predicates and `formatDeviceName` stay
 * catalog-driven. Returns the index of capability-by-type for component use.
 */
export const useDeviceCatalog = (): DeviceCatalogIndex => {
  const { data } = useQuery<DeviceCatalogResponse>(
    [QueryKeys.DEVICE_CATALOG],
    () => api.get<DeviceCatalogResponse>("/v2/device-types"),
    { staleTime: 1000 * 60 * 60 }, // 1h — the catalog rarely changes within a session
  );

  const index = useMemo(() => (data ? indexByType(data) : {}), [data]);

  // Mirror into the module cache during render (not in an effect): the synchronous readers
  // (formatDeviceName, device predicates) run in the SAME render that react-query re-renders
  // subscribers when the catalog loads, so the cache must already be fresh by then. An effect would
  // update it one commit too late — that re-render would still read the empty cache and nothing would
  // schedule another. The write is idempotent (same memoised index), so repeating it per render is safe.
  setDeviceCatalogCache(index);

  return index;
};
